import { useEffect, useRef, useState } from 'react';
import { Button, Input, message } from 'antd';
import client from '../api/client';

export type SmsScene = 'register' | 'login' | 'reset_password' | 'change_phone';

interface Props {
  scene: SmsScene;
  phoneNumber: string;
  size?: 'middle' | 'large';
  onGet?: (debugCode?: string) => void;
  /** antd Form.Item 注入的受控值（name="smsCode" 时生效） */
  value?: string;
  /** antd Form.Item 注入的变更回调 */
  onChange?: (value: string) => void;
}

const CODE_TTL_MS = 6 * 60 * 1000; // 与后端 6 分钟有效期对齐

/**
 * 短信验证码组件：前端不生成验证码（由后端生成并短信下发），
 * 只负责 获取/输入/60s 倒计时/SMS_DEBUG 回显/超时重新认证。
 * 支持 antd Form 受控协议（Form.Item name="smsCode" 包裹时自动生效）。
 */
export default function SmsCodeInput({ scene, phoneNumber, size = 'large', onGet, value, onChange }: Props) {
  const [countdown, setCountdown] = useState(0);
  const [sendAt, setSendAt] = useState<number | null>(null);
  const [internalCode, setInternalCode] = useState('');
  const [sending, setSending] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 受控判断：Form.Item 注入 onChange 时一律通过 onChange 上报（value 初始为 undefined 也算受控），
  // 否则内部 state 兜底（组件独立使用场景）
  const code = onChange ? (value ?? '') : internalCode;
  const setCode = (v: string) => {
    if (onChange) onChange(v);
    else setInternalCode(v);
  };

  const phoneValid = /^1[3-9]\d{9}$/.test(phoneNumber);
  const expired = sendAt !== null && Date.now() - sendAt >= CODE_TTL_MS;

  useEffect(() => {
    if (countdown <= 0) return;
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [countdown > 0]);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const handleGet = async () => {
    setSending(true);
    try {
      const res = await client.post('/auth/sms/send', { phoneNumber, scene });
      const debugCode = res.data?.debugCode as string | undefined;
      setSendAt(Date.now());
      setCode('');
      setCountdown(60);
      if (debugCode) {
        message.info(`【测试模式】验证码：${debugCode}`);
      } else {
        message.success('验证码已发送，请注意查收');
      }
      onGet?.(debugCode);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || '发送失败';
      message.error(msg);
    } finally {
      setSending(false);
    }
  };

  const expiredHint = expired ? '验证码已过期，请重新获取' : undefined;

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <Input
        value={code}
        disabled={expired}
        onChange={(e) => setCode(e.target.value)}
        status={expired ? 'error' : undefined}
        placeholder="请输入6位验证码"
        maxLength={6}
        inputMode="numeric"
        autoComplete="one-time-code"
        style={{ flex: 1, letterSpacing: 6, fontSize: 18 }}
        size={size === 'large' ? 'large' : 'middle'}
        title={expiredHint}
      />
      <Button
        size={size}
        disabled={!phoneValid || countdown > 0}
        loading={sending}
        onClick={handleGet}
        style={{ minWidth: 130 }}
      >
        {countdown > 0 ? `重新获取(${countdown}s)` : expired ? '重新获取' : '获取验证码'}
      </Button>
    </div>
  );
}